"use client"

import { useState } from "react"
import { Copy, Printer, Share2 } from "lucide-react"

const RESTAURANT_TYPES = {
   "Fine Dining": { target: 25, min: 22, max: 28 },
   "Casual Dining": { target: 30, min: 25, max: 35 },
   "Fast Casual": { target: 28, min: 25, max: 32 },
   "Food Truck": { target: 32, min: 28, max: 36 },
   Catering: { target: 30, min: 25, max: 35 },
   "Cafe / Bakery": { target: 27, min: 22, max: 32 },
} as const

type RoundingType = "natural" | "round" | "exact"
type RestaurantTypeKey = keyof typeof RESTAURANT_TYPES

const BENCHMARK_TARGETS = [22, 25, 28, 30, 32, 35]

const ROUNDING_OPTIONS: { key: RoundingType; label: string }[] = [
   { key: "natural", label: "Natural (.95)" },
   { key: "round", label: "Round ($)" },
   { key: "exact", label: "Exact" },
]

function applyRounding(price: number, type: RoundingType): number {
   if (!isFinite(price) || isNaN(price) || price <= 0) return 0
   switch (type) {
      case "natural":
         return Math.ceil(price) - 0.05
      case "round":
         return Math.ceil(price)
      case "exact":
         return price
   }
}

function fmt(val: number, decimals = 2): string {
   return val.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
   })
}

export default function MenuPricingCalculator() {
   const [restaurantType, setRestaurantType] = useState<RestaurantTypeKey>("Casual Dining")
   const [itemName, setItemName] = useState("")
   const [ingredientCost, setIngredientCost] = useState("")
   const [targetPercent, setTargetPercent] = useState("30")
   const [rounding, setRounding] = useState<RoundingType>("round")
   const [hasCalculated, setHasCalculated] = useState(false)
   const [resultCopied, setResultCopied] = useState(false)
   const [shareCopied, setShareCopied] = useState(false)

   const cost = parseFloat(ingredientCost) || 0
   const target = Math.max(parseFloat(targetPercent) || 1, 0.01)
   const typeData = RESTAURANT_TYPES[restaurantType]

   const rawPrice = cost > 0 ? cost / (target / 100) : 0
   const recommendedPrice = applyRounding(rawPrice, rounding)
   const actualFoodCostPercent = recommendedPrice > 0 ? (cost / recommendedPrice) * 100 : 0
   const grossProfit = recommendedPrice - cost
   const grossProfitMargin = recommendedPrice > 0 ? (grossProfit / recommendedPrice) * 100 : 0

   function handleTypeChange(type: RestaurantTypeKey) {
      setRestaurantType(type)
      setTargetPercent(String(RESTAURANT_TYPES[type].target))
   }

   function handleReset() {
      setHasCalculated(false)
      setItemName("")
      setIngredientCost("")
      setTargetPercent("30")
      setRounding("round")
      setRestaurantType("Casual Dining")
   }

   const canCalculate = cost > 0

   function copyResult() {
      if (!hasCalculated) return
      navigator.clipboard
         .writeText(
            [
               "Menu Pricing Calculator — restaurantassociation.com",
               itemName ? `Item: ${itemName}` : null,
               `Recommended Price: $${fmt(recommendedPrice)}`,
               `Food Cost %: ${fmt(actualFoodCostPercent)}%`,
               `Gross Profit: $${fmt(grossProfit)}`,
               `Gross Profit Margin: ${fmt(grossProfitMargin)}%`,
            ]
               .filter(Boolean)
               .join("\n"),
         )
         .catch(() => null)
      setResultCopied(true)
      setTimeout(() => setResultCopied(false), 2000)
   }

   async function shareLink() {
      const url = window.location.href
      if (navigator.share) {
         try {
            await navigator.share({ title: "Menu Pricing Calculator", text: "Calculate the optimal price for your menu items.", url })
            return
         } catch {
            // cancelled or unsupported — fall through
         }
      }
      await navigator.clipboard.writeText(url).catch(() => null)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
   }

   return (
      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
         <div className="border-b border-gray-100 px-6 py-5">
            <h2 className="text-custom-3xl-bold text-gray-900">Price Your Menu Item</h2>
            <p className="mt-0.5 text-custom-lg-normal text-gray-500">
               Enter your ingredient cost and target percentage to calculate your optimal price.
            </p>
         </div>

         <div className="p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
               {/* Restaurant Type */}
               <div>
                  <label className="mb-1.5 block text-custom-sm-bold uppercase text-gray-700">
                     Restaurant Type
                  </label>
                  <div className="relative">
                     <select
                        className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 focus:border-[#196EB9] focus:outline-none focus:ring-2 focus:ring-[#196EB9]/20"
                        value={restaurantType}
                        onChange={(e) => handleTypeChange(e.target.value as RestaurantTypeKey)}
                     >
                        {(Object.keys(RESTAURANT_TYPES) as RestaurantTypeKey[]).map((type) => (
                           <option key={type} value={type}>
                              {type}
                           </option>
                        ))}
                     </select>
                     <svg
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                     >
                        <path
                           fillRule="evenodd"
                           d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                           clipRule="evenodd"
                        />
                     </svg>
                  </div>
                  <p className="mt-1.5 text-custom-sm-normal text-gray-400">
                     Recommended for {restaurantType}: {typeData.min}%–{typeData.max}%
                  </p>
               </div>

               {/* Menu Item Name */}
               <div>
                  <label className="mb-1.5 block text-custom-sm-bold uppercase text-gray-700">
                     Menu Item Name{" "}
                     <span className="text-custom-sm-bold  normal-case text-gray-400">(Optional)</span>
                  </label>
                  <input
                     type="text"
                     placeholder="e.g. Signature Burger"
                     className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder-gray-300 focus:border-[#196EB9] focus:outline-none focus:ring-2 focus:ring-[#196EB9]/20"
                     value={itemName}
                     onChange={(e) => setItemName(e.target.value)}
                  />
                  <p className="mt-1.5 text-custom-sm-normal text-gray-400">Used for labelling your result.</p>
               </div>

               {/* Ingredient Cost */}
               <div>
                  <label className="mb-1.5 block text-custom-sm-bold uppercase text-gray-700">
                     Total Ingredient Cost / Serving
                  </label>
                  <div className="relative">
                     <span className="absolute left-4 top-1/2 -translate-y-1/2 font-semibold text-gray-400">$</span>
                     <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full rounded-xl border border-gray-200 py-3 pl-8 pr-4 text-sm text-gray-900 placeholder-gray-300 focus:border-[#196EB9] focus:outline-none focus:ring-2 focus:ring-[#196EB9]/20"
                        value={ingredientCost}
                        onChange={(e) => {
                           if (parseFloat(e.target.value) >= 0 || e.target.value === "") {
                              setIngredientCost(e.target.value)
                           }
                        }}
                     />
                  </div>
                  <p className="mt-1.5 text-custom-sm-normal text-gray-400">All ingredients including garnish & sides.</p>
               </div>

               {/* Target Food Cost % */}
               <div>
                  <label className="mb-1.5 block text-custom-sm-bold uppercase text-gray-700">
                     Target Food Cost Percentage
                  </label>
                  <div className="relative">
                     <input
                        type="number"
                        min="1"
                        max="100"
                        step="0.1"
                        className="w-full rounded-xl border border-gray-200 py-3 pl-4 pr-10 text-sm text-gray-900 focus:border-[#196EB9] focus:outline-none focus:ring-2 focus:ring-[#196EB9]/20"
                        value={targetPercent}
                        onChange={(e) => {
                           if (parseFloat(e.target.value) >= 0 || e.target.value === "") {
                              setTargetPercent(e.target.value)
                           }
                        }}
                     />
                     <span className="absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-gray-400">%</span>
                  </div>
                  <p className="mt-1.5 text-custom-sm-normal text-gray-400">Adjust to see different price models.</p>
               </div>
            </div>

            {/* Rounding tabs */}
            <div className="mt-4">
               <label className="mb-2 block text-custom-sm-bold uppercase text-gray-700">
                  Price Rounding Convention
               </label>
               <div className="flex overflow-hidden rounded-xl border border-gray-200">
                  {ROUNDING_OPTIONS.map(({ key, label }, i) => (
                     <button
                        key={key}
                        className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                           rounding === key
                              ? "bg-[#196EB9] text-white"
                              : "bg-gray-50 text-gray-500 hover:bg-[#EAF5FF] hover:text-[#196EB9]"
                        } ${i > 0 ? "border-l border-gray-200" : ""}`}
                        onClick={() => setRounding(key)}
                     >
                        {label}
                     </button>
                  ))}
               </div>
               <p className="mt-1.5 text-custom-sm-normal text-gray-400">
                  Natural (.95) is common in casual dining, Round ($) in fine dining.
               </p>
            </div>

            {/* Buttons */}
            <button
               className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#EB5A23] text-custom-base-bold text-white transition-colors hover:bg-[#cf4e1e] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
               onClick={() => setHasCalculated(true)}
               disabled={!canCalculate}
            >
               Calculate Menu Price
            </button>
            <button
               className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-custom-sm-bold  text-gray-600 transition-colors hover:bg-gray-50"
               onClick={handleReset}
            >
               Reset Calculator
            </button>

            {/* Results */}
            <div className="mt-8">
               {!hasCalculated ? (
                  <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-12 text-center">
                     <svg
                        className="mx-auto mb-4 h-10 w-10 text-gray-300"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                     >
                        <path
                           d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        />
                     </svg>
                     <h4 className="mb-1 text-custom-base-bold text-gray-500">Ready to calculate</h4>
                     <p className="text-custom-sm-normal text-gray-400">Enter your figures above to see your recommended price.</p>
                  </div>
               ) : (
                  <div>
                     {/* Recommended price */}
                     <div className="mb-6">
                        <p className="mb-1 text-xs font-bold uppercase tracking-widest text-gray-400">
                           {itemName ? `${itemName.toUpperCase()} — ` : ""}RECOMMENDED PRICE
                        </p>
                        <p className="font-serif text-5xl font-bold text-gray-900">${fmt(recommendedPrice)}</p>
                     </div>

                     {/* 4-cell metrics grid */}
                     <div className="mb-6 grid grid-cols-2 overflow-hidden rounded-xl border border-gray-100">
                        {[
                           {
                              label: "Actual Food Cost",
                              value: `${fmt(actualFoodCostPercent, 1)}%`,
                              sub: "at this price",
                           },
                           {
                              label: "Gross Profit",
                              value: `$${fmt(grossProfit)}`,
                              sub: `${fmt(grossProfitMargin, 1)}% margin`,
                           },
                           {
                              label: "Ingredient Cost",
                              value: `$${fmt(cost)}`,
                              sub: "per serving",
                           },
                           {
                              label: "Raw Calc Price",
                              value: `$${fmt(rawPrice)}`,
                              sub: "unrounded base",
                           },
                        ].map((cell, i) => (
                           <div
                              key={i}
                              className={`px-4 py-4 text-center ${i % 2 === 0 ? "border-r border-gray-100" : ""} ${i < 2 ? "border-b border-gray-100" : ""}`}
                           >
                              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-400">
                                 {cell.label}
                              </p>
                              <p className="font-serif text-xl font-bold text-gray-900">{cell.value}</p>
                              <p className="mt-0.5 text-xs text-gray-400">{cell.sub}</p>
                           </div>
                        ))}
                     </div>

                     {/* Benchmark target comparison */}
                     <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">
                           Price at different food cost targets
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                           {BENCHMARK_TARGETS.map((bm) => {
                              const bmRaw = cost > 0 ? cost / (bm / 100) : 0
                              const bmPrice = applyRounding(bmRaw, rounding)
                              const isActive = parseFloat(targetPercent) === bm
                              return (
                                 <button
                                    key={bm}
                                    className={`rounded-lg border px-2 py-3 text-center transition-colors ${
                                       isActive
                                          ? "border-[#196EB9] bg-[#EAF5FF] text-[#085DA7]"
                                          : "border-gray-200 bg-white text-gray-600 hover:border-[#196EB9] hover:bg-[#EAF5FF] hover:text-[#085DA7]"
                                    }`}
                                    onClick={() => setTargetPercent(String(bm))}
                                 >
                                    <div
                                       className={`text-xs font-bold uppercase ${isActive ? "text-[#085DA7]" : "text-gray-400"}`}
                                    >
                                       {bm}% target
                                    </div>
                                    <div
                                       className={`mt-0.5 font-serif text-lg font-bold ${isActive ? "text-[#085DA7]" : "text-gray-900"}`}
                                    >
                                       ${fmt(bmPrice)}
                                    </div>
                                 </button>
                              )
                           })}
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </div>

         <div className="grid grid-cols-3 gap-3 border-t border-gray-100 p-6 pt-5">
            {[
               { label: resultCopied ? "Copied!" : "Copy Result", icon: <Copy className="h-4 w-4" />, onClick: copyResult },
               { label: shareCopied ? "Link Copied!" : "Share Link", icon: <Share2 className="h-4 w-4" />, onClick: shareLink },
               { label: "Print", icon: <Printer className="h-4 w-4" />, onClick: () => window.print() },
            ].map(({ label, icon, onClick }) => (
               <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-600 transition hover:border-[#196EB9] hover:text-[#196EB9]"
               >
                  {icon}
                  <span className="hidden sm:inline">{label}</span>
               </button>
            ))}
         </div>
      </div>
   )
}
